import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Image } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useNotificationStore } from '@/stores/notificationStore';
import { mediaService } from '@/services/mediaService';
import type { MediaItem } from '@/types/blog';

export function AdminMediaLibrary() {
  const notify = useNotificationStore();
  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageSize = 24;

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const result = await mediaService.list({
        search: search || undefined,
        page,
        pageSize,
      });
      setItems(result.data);
      setTotal(result.total);
    } catch (err: any) {
      notify.error(err.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      let count = 0;
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          notify.error(`${file.name} is not an image`);
          continue;
        }
        await mediaService.uploadFile(file);
        count++;
      }
      if (count > 0) {
        notify.success(`${count} image${count > 1 ? 's' : ''} uploaded`);
        setPage(1);
        loadMedia();
      }
    } catch (err: any) {
      notify.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} item${selected.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await mediaService.bulkDelete(Array.from(selected));
      notify.success(`${selected.size} item${selected.size > 1 ? 's' : ''} deleted`);
      setSelected(new Set());
      loadMedia();
    } catch (err: any) {
      notify.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Media Library"
        description={`${total} item${total !== 1 ? 's' : ''} in library`}
      >
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
              isLoading={deleting}
            >
              Delete ({selected.size})
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            isLoading={uploading}
          >
            Upload Image
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>
      </PageHeader>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <Input
          placeholder="Search images..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Image className="mx-auto h-16 w-16 text-muted-foreground/30 mb-4" />
          <p className="text-lg font-medium">No images yet</p>
          <p className="text-sm mt-1">Upload images to start building your media library.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload Image
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {items.map((item) => {
              const isSelected = selected.has(item.id);
              return (
                <div
                  key={item.id}
                  className={`relative group rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-transparent hover:border-border'
                  }`}
                  onClick={() => toggleSelect(item.id)}
                >
                  <div className="aspect-square">
                    <img
                      src={item.url}
                      alt={item.alt_text || item.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2 bg-background">
                    <p className="text-xs font-medium text-foreground truncate">{item.original_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.width && item.height ? `${item.width}×${item.height}` : ''}
                      {item.file_size ? ` · ${formatFileSize(item.file_size)}` : ''}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shadow">
                      ✓
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
